<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { useSupervisorPauses } from 'dashboard/composables/useSupervisorPauses';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';

const { t } = useI18n();

const {
  pendingRequests,
  pauses,
  pendingCount,
  activeCount,
  completedTodayCount,
  isFetching,
  loadingActionId,
  approveRequest,
  rejectRequest,
} = useSupervisorPauses();

const activePauses = computed(() =>
  pauses.value.filter(pause => pause.status === 'active')
);
const historyPauses = computed(() =>
  pauses.value.filter(pause => pause.status !== 'active')
);

const summaryCards = computed(() => [
  {
    label: t('AGENT_MGMT.PAUSES.SUMMARY.PENDING'),
    value: pendingCount.value,
    icon: 'i-lucide-bell-ring',
    tone: pendingCount.value
      ? 'text-n-amber-11 bg-n-amber-5/70'
      : 'text-n-slate-11 bg-n-alpha-2',
  },
  {
    label: t('AGENT_MGMT.PAUSES.SUMMARY.ACTIVE'),
    value: activeCount.value,
    icon: 'i-lucide-timer',
    tone: activeCount.value
      ? 'text-n-teal-11 bg-n-teal-5/70'
      : 'text-n-slate-11 bg-n-alpha-2',
  },
  {
    label: t('AGENT_MGMT.PAUSES.SUMMARY.COMPLETED_TODAY'),
    value: completedTodayCount.value,
    icon: 'i-lucide-history',
    tone: 'text-n-slate-12 bg-n-alpha-2',
  },
]);

const formatDateTime = isoString => {
  if (!isoString) return '—';

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(isoString));
};

const formatDuration = minutes => {
  return `${minutes} min`;
};

const pauseStatusLabel = pause => {
  if (pause.overdue) {
    return t('AGENT_MGMT.PAUSES.STATUS.OVERDUE');
  }

  if (pause.status === 'active') {
    return t('AGENT_MGMT.PAUSES.STATUS.ACTIVE');
  }

  if (pause.status === 'completed') {
    return t('AGENT_MGMT.PAUSES.STATUS.COMPLETED');
  }

  if (pause.status === 'rejected') {
    return t('AGENT_MGMT.PAUSES.STATUS.REJECTED');
  }

  if (pause.status === 'canceled') {
    return t('AGENT_MGMT.PAUSES.STATUS.CANCELED');
  }

  return pause.status;
};

const pauseStatusClass = pause => {
  if (pause.overdue) {
    return 'bg-n-ruby-5 text-n-ruby-11 outline-n-ruby-7/60';
  }

  if (pause.status === 'active') {
    return 'bg-n-teal-5 text-n-teal-11 outline-n-teal-8/60';
  }

  if (pause.status === 'completed') {
    return 'bg-n-slate-4 text-n-slate-12 outline-n-strong/50';
  }

  if (pause.status === 'rejected' || pause.status === 'canceled') {
    return 'bg-n-ruby-5 text-n-ruby-11 outline-n-ruby-7/60';
  }

  return 'bg-n-amber-5 text-n-amber-11 outline-n-amber-8/60';
};

const showTimingBadge = pause => pause.timing_status !== 'not_applicable';

const pauseTimingLabel = pause => {
  if (pause.timing_status === 'exceeded') {
    return t('AGENT_MGMT.PAUSES.TIMING.EXCEEDED');
  }

  return t('AGENT_MGMT.PAUSES.TIMING.WITHIN_TIME');
};

const pauseTimingClass = pause => {
  if (pause.timing_status === 'exceeded') {
    return 'bg-n-ruby-5 text-n-ruby-11 outline-n-ruby-7/60';
  }

  return 'bg-n-teal-5 text-n-teal-11 outline-n-teal-8/60';
};

const activeTimingLabel = pause =>
  pause.overdue
    ? t('AGENT_MGMT.PAUSES.ACTIVE.OVERDUE_SINCE')
    : t('AGENT_MGMT.PAUSES.ACTIVE.PLANNED_END');

const historyTimestampLabel = pause => {
  if (pause.completed_at) return t('AGENT_MGMT.PAUSES.HISTORY.ENDED_AT');
  if (pause.canceled_at) return t('AGENT_MGMT.PAUSES.HISTORY.CANCELED_AT');
  if (pause.rejected_at) return t('AGENT_MGMT.PAUSES.HISTORY.REJECTED_AT');

  return t('AGENT_MGMT.PAUSES.HISTORY.PLANNED_END');
};

const historyTimestampValue = pause => {
  return (
    pause.completed_at ||
    pause.canceled_at ||
    pause.rejected_at ||
    pause.ends_at
  );
};
</script>

<template>
  <SettingsLayout
    :is-loading="isFetching"
    :loading-message="$t('AGENT_MGMT.PAUSES.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('AGENT_MGMT.PAUSES.PAGE_TITLE')"
        :description="$t('AGENT_MGMT.PAUSES.PAGE_DESCRIPTION')"
      />
    </template>

    <template #body>
      <div class="flex flex-col gap-6 w-full">
        <div class="grid gap-4 lg:grid-cols-3">
          <div
            v-for="card in summaryCards"
            :key="card.label"
            class="rounded-2xl bg-n-surface-2 p-5 outline outline-1 outline-n-weak"
          >
            <div class="flex items-start justify-between gap-3">
              <div>
                <div class="text-sm text-n-slate-11">
                  {{ card.label }}
                </div>
                <div class="mt-3 text-3xl font-semibold text-n-slate-12">
                  {{ card.value }}
                </div>
              </div>
              <div
                class="grid size-11 place-items-center rounded-2xl"
                :class="card.tone"
              >
                <Icon :icon="card.icon" class="size-5" />
              </div>
            </div>
          </div>
        </div>

        <section
          class="rounded-[28px] bg-n-surface-2 p-6 outline outline-1 outline-n-weak"
        >
          <div class="flex flex-col gap-1">
            <div class="text-lg font-semibold text-n-slate-12">
              {{ $t('AGENT_MGMT.PAUSES.PENDING.TITLE') }}
            </div>
            <div class="text-sm text-n-slate-11">
              {{ $t('AGENT_MGMT.PAUSES.PENDING.DESCRIPTION') }}
            </div>
          </div>

          <div
            v-if="!pendingRequests.length"
            class="mt-5 rounded-2xl bg-n-alpha-2 p-6 text-sm text-n-slate-11"
          >
            {{ $t('AGENT_MGMT.PAUSES.PENDING.EMPTY') }}
          </div>

          <div v-else class="mt-5 grid gap-4">
            <article
              v-for="request in pendingRequests"
              :key="request.id"
              class="rounded-2xl bg-gradient-to-r from-n-amber-5/35 via-n-surface-1 to-n-surface-1 p-5 outline outline-1 outline-n-weak"
            >
              <div
                class="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between"
              >
                <div class="flex items-start gap-4 min-w-0">
                  <Avatar :name="request.agent.name" :size="40" />
                  <div class="min-w-0">
                    <div class="text-base font-medium text-n-slate-12">
                      {{
                        $t('AGENT_MGMT.PAUSES.PENDING.REQUEST_TITLE', {
                          agentName: request.agent.name,
                          label: request.label,
                        })
                      }}
                    </div>
                    <div class="mt-1 text-sm text-n-slate-11">
                      {{ request.agent.email }}
                    </div>
                    <div class="mt-3 flex flex-wrap gap-2">
                      <span
                        class="rounded-full bg-n-alpha-2 px-2.5 py-1 text-xs text-n-slate-11"
                      >
                        {{
                          $t('AGENT_MGMT.PAUSES.PENDING.REQUESTED_AT', {
                            time: formatDateTime(request.requested_at),
                          })
                        }}
                      </span>
                      <span
                        class="rounded-full bg-n-alpha-2 px-2.5 py-1 text-xs text-n-slate-11"
                      >
                        {{
                          $t('AGENT_MGMT.PAUSES.PENDING.DURATION', {
                            count: request.duration_in_minutes,
                          })
                        }}
                      </span>
                    </div>
                  </div>
                </div>

                <div class="flex gap-2 shrink-0">
                  <Button
                    slate
                    :label="$t('AGENT_MGMT.PAUSES.PENDING.REJECT')"
                    :is-loading="loadingActionId === request.id"
                    @click="rejectRequest(request)"
                  />
                  <Button
                    :label="$t('AGENT_MGMT.PAUSES.PENDING.APPROVE')"
                    :is-loading="loadingActionId === request.id"
                    @click="approveRequest(request)"
                  />
                </div>
              </div>
            </article>
          </div>
        </section>

        <div class="grid gap-6 xl:grid-cols-[0.95fr_1.05fr]">
          <section
            class="rounded-[28px] bg-n-surface-2 p-6 outline outline-1 outline-n-weak"
          >
            <div class="flex flex-col gap-1">
              <div class="text-lg font-semibold text-n-slate-12">
                {{ $t('AGENT_MGMT.PAUSES.ACTIVE.TITLE') }}
              </div>
              <div class="text-sm text-n-slate-11">
                {{ $t('AGENT_MGMT.PAUSES.ACTIVE.DESCRIPTION') }}
              </div>
            </div>

            <div
              v-if="!activePauses.length"
              class="mt-5 rounded-2xl bg-n-alpha-2 p-6 text-sm text-n-slate-11"
            >
              {{ $t('AGENT_MGMT.PAUSES.ACTIVE.EMPTY') }}
            </div>

            <div v-else class="mt-5 grid gap-3">
              <article
                v-for="pause in activePauses"
                :key="pause.id"
                class="rounded-2xl bg-n-surface-1 p-5 outline outline-1 outline-n-weak"
              >
                <div class="flex items-start justify-between gap-3">
                  <div class="min-w-0">
                    <div class="text-base font-medium text-n-slate-12">
                      {{ pause.agent.name }}
                    </div>
                    <div class="mt-1 text-sm text-n-slate-11">
                      {{ pause.label }}
                    </div>
                  </div>
                  <span
                    class="rounded-full px-2.5 py-1 text-xs font-medium outline outline-1"
                    :class="pauseStatusClass(pause)"
                  >
                    {{ pauseStatusLabel(pause) }}
                  </span>
                </div>

                <div class="mt-4 grid gap-3 sm:grid-cols-2">
                  <div class="rounded-xl bg-n-alpha-2 px-3 py-2">
                    <div
                      class="text-xs uppercase tracking-wide text-n-slate-10"
                    >
                      {{ $t('AGENT_MGMT.PAUSES.ACTIVE.STARTED_AT') }}
                    </div>
                    <div class="mt-1 text-sm text-n-slate-12">
                      {{ formatDateTime(pause.started_at) }}
                    </div>
                  </div>
                  <div class="rounded-xl bg-n-alpha-2 px-3 py-2">
                    <div
                      class="text-xs uppercase tracking-wide text-n-slate-10"
                    >
                      {{ activeTimingLabel(pause) }}
                    </div>
                    <div class="mt-1 text-sm text-n-slate-12">
                      {{ formatDateTime(pause.ends_at) }}
                    </div>
                  </div>
                </div>
              </article>
            </div>
          </section>

          <section
            class="rounded-[28px] bg-n-surface-2 p-6 outline outline-1 outline-n-weak"
          >
            <div class="flex flex-col gap-1">
              <div class="text-lg font-semibold text-n-slate-12">
                {{ $t('AGENT_MGMT.PAUSES.HISTORY.TITLE') }}
              </div>
              <div class="text-sm text-n-slate-11">
                {{ $t('AGENT_MGMT.PAUSES.HISTORY.DESCRIPTION') }}
              </div>
            </div>

            <div
              v-if="!historyPauses.length"
              class="mt-5 rounded-2xl bg-n-alpha-2 p-6 text-sm text-n-slate-11"
            >
              {{ $t('AGENT_MGMT.PAUSES.HISTORY.EMPTY') }}
            </div>

            <div v-else class="mt-5 grid gap-3">
              <article
                v-for="pause in historyPauses"
                :key="pause.id"
                class="rounded-2xl bg-n-surface-1 p-5 outline outline-1 outline-n-weak"
              >
                <div class="flex items-start justify-between gap-3">
                  <div class="min-w-0">
                    <div class="text-base font-medium text-n-slate-12">
                      {{ pause.agent.name }}
                    </div>
                    <div class="mt-1 text-sm text-n-slate-11">
                      {{ pause.label }}
                    </div>
                  </div>
                  <div class="flex flex-wrap items-center justify-end gap-2">
                    <span
                      v-if="showTimingBadge(pause)"
                      class="rounded-full px-2.5 py-1 text-xs font-medium outline outline-1"
                      :class="pauseTimingClass(pause)"
                    >
                      {{ pauseTimingLabel(pause) }}
                    </span>
                    <span
                      class="rounded-full px-2.5 py-1 text-xs font-medium outline outline-1"
                      :class="pauseStatusClass(pause)"
                    >
                      {{ pauseStatusLabel(pause) }}
                    </span>
                  </div>
                </div>

                <div class="mt-4 grid gap-3 sm:grid-cols-3">
                  <div class="rounded-xl bg-n-alpha-2 px-3 py-2">
                    <div
                      class="text-xs uppercase tracking-wide text-n-slate-10"
                    >
                      {{ $t('AGENT_MGMT.PAUSES.HISTORY.REQUESTED_AT') }}
                    </div>
                    <div class="mt-1 text-sm text-n-slate-12">
                      {{ formatDateTime(pause.requested_at) }}
                    </div>
                  </div>
                  <div class="rounded-xl bg-n-alpha-2 px-3 py-2">
                    <div
                      class="text-xs uppercase tracking-wide text-n-slate-10"
                    >
                      {{ $t('AGENT_MGMT.PAUSES.HISTORY.DURATION') }}
                    </div>
                    <div class="mt-1 text-sm text-n-slate-12">
                      {{ formatDuration(pause.duration_in_minutes) }}
                    </div>
                  </div>
                  <div class="rounded-xl bg-n-alpha-2 px-3 py-2">
                    <div
                      class="text-xs uppercase tracking-wide text-n-slate-10"
                    >
                      {{ historyTimestampLabel(pause) }}
                    </div>
                    <div class="mt-1 text-sm text-n-slate-12">
                      {{ formatDateTime(historyTimestampValue(pause)) }}
                    </div>
                  </div>
                </div>
              </article>
            </div>
          </section>
        </div>
      </div>
    </template>
  </SettingsLayout>
</template>
